/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import React, { Fragment, useState } from 'react'

import {
  EuiButton,
  EuiContextMenuItem,
  EuiContextMenuPanel,
  EuiPopover,
  EuiTextColor,
  useGeneratedHtmlId,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { useToggle } from '@modules/utils/hooks/useToggle'
import type { AnyCluster } from '@modules/ccm-lib/types'
import { DisconnectClusterModal } from '@modules/ccm-components/DisconnectClusterModal'

import type { FC } from 'react'

type Props = {
  ccmCluster: AnyCluster
  onClickDisconnectCluster: (clusterId: string) => void
}

export const CCMClusterActionsButton: FC<Props> = ({ ccmCluster, onClickDisconnectCluster }) => {
  const menuPopoverId = useGeneratedHtmlId({
    prefix: `menuPopover-${ccmCluster.id}`,
  })

  const [isPopoverOpen, togglePopoverOpen] = useToggle(false)

  const [isDisconnectingCluster, setIsDisconnectingCluster] = useState(false)

  const menuItems = [
    <EuiContextMenuItem
      key='edit'
      onClick={() => {
        togglePopoverOpen()

        setIsDisconnectingCluster(true)
      }}
    >
      <EuiTextColor color='danger'>
        <FormattedMessage
          id='ccm.cluster-page.actions.disconnect-cluster-button'
          defaultMessage='Disconnect cluster'
        />
      </EuiTextColor>
    </EuiContextMenuItem>,
  ]

  return (
    <Fragment>
      <EuiPopover
        id={menuPopoverId}
        button={
          <EuiButton iconType='arrowDown' iconSide='right' onClick={togglePopoverOpen} fill={true}>
            <FormattedMessage id='ccm.cluster-page.actions-button' defaultMessage='Actions' />
          </EuiButton>
        }
        isOpen={isPopoverOpen}
        closePopover={togglePopoverOpen}
        panelPaddingSize='none'
        anchorPosition='downLeft'
      >
        <EuiContextMenuPanel size='s' items={menuItems} />
      </EuiPopover>
      {isDisconnectingCluster && (
        <DisconnectClusterModal
          disconnectingCluster={ccmCluster}
          onConfirm={() => {
            onClickDisconnectCluster(ccmCluster.id)
          }}
          onCancel={() => {
            setIsDisconnectingCluster(false)
          }}
        />
      )}
    </Fragment>
  )
}
